import { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@wedding/ui";
import { Copy, Download, QrCode, X } from "lucide-react";

export type QrCodeTarget = {
  title: string;
  url: string;
};

async function copyText(value: string) {
  if (navigator.clipboard?.writeText)
    return navigator.clipboard.writeText(value);
  const input = document.createElement("textarea");
  input.value = value;
  input.style.position = "fixed";
  input.style.opacity = "0";
  document.body.appendChild(input);
  input.select();
  document.execCommand("copy");
  input.remove();
}

export function QrCodeDialog({
  target,
  onClose,
}: {
  target: QrCodeTarget;
  onClose(): void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    dialogRef.current?.querySelector<HTMLElement>("button")?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const download = () => {
    const svg = dialogRef.current?.querySelector("svg");
    if (!svg) return;
    const source = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([source], { type: "image/svg+xml;charset=utf-8" });
    const anchor = document.createElement("a");
    anchor.href = URL.createObjectURL(blob);
    anchor.download = `${target.title.replace(/[\\/:*?"<>|]/g, "-")}-二维码.svg`;
    anchor.click();
    URL.revokeObjectURL(anchor.href);
  };

  return (
    <div className="drawer-backdrop qr-dialog-backdrop">
      <div
        ref={dialogRef}
        className="qr-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={`${target.title}二维码`}
      >
        <header>
          <div>
            <span className="eyebrow">扫码打开 RSVP</span>
            <h2>{target.title}</h2>
          </div>
          <button
            className="icon-button"
            aria-label="关闭二维码"
            onClick={onClose}
          >
            <X />
          </button>
        </header>
        <div className="qr-preview">
          <QRCodeSVG
            value={target.url}
            size={260}
            level="H"
            marginSize={4}
            title={`${target.title} RSVP 二维码`}
          />
        </div>
        <p>宾客扫码后可登记，之后再次打开同一专属链接也能修改信息。</p>
        <code>{target.url}</code>
        <footer>
          <Button
            className="secondary"
            onClick={async () => {
              await copyText(target.url);
              setCopied(true);
            }}
          >
            <Copy size={16} /> {copied ? "链接已复制" : "复制链接"}
          </Button>
          <Button onClick={download}>
            <Download size={16} /> 下载二维码
          </Button>
        </footer>
        <QrCode className="qr-corner-mark" aria-hidden="true" />
      </div>
    </div>
  );
}
